import { useEffect } from 'react';
import {
  CurrencyInput,
  DateInput,
  FormInputWrapper,
  PostcodeInput,
  TextInput,
} from 'form/infrastructure/reactHookForm';
import { PropertyId as Id } from 'modules/cm/api/cases/repaymentStrategy/property/Property';
import { CaseId } from 'modules/cm/api/cases/types';
import {
  createProperty,
  Property,
  PropertyField,
  RepaymentStrategyField,
} from 'modules/cm/RepaymentStrategy/RepaymentStrategy';
import { useFieldArray, useFormContext } from 'react-hook-form';
import styled from 'styled-components';
import { FieldsLabels } from '../RepaymentStrategy';

const ActionButton = styled.button`
  color: var(--color-tangerine);
  font-weight: 700;
  text-decoration: underline;
  font-weight: 700;
  background: transparent;
  outline: none;
`;

const PropertyContainer = styled.div`
  margin-bottom: 32px;
`;

const ItemContainer = styled.div`
  &:last-of-type {
    margin-bottom: 32px;
  }
`;

const HorizontalRuller = styled.hr`
  margin: 32px 0;
  border-top: 1px solid var(--color-smoke-30);
`;

export type FormProperty = Pick<Property, PropertyField.Id> & Partial<Omit<Property, PropertyField.Id>>;

type OnRemoveAction = (id: Id) => void;

interface PropertyProps {
  index: number;
  onRemove?: OnRemoveAction;
  item: Property;
}

const PropertyItem = ({ index, onRemove, item }: PropertyProps) => {
  const canRemoveItem = (onRemove: OnRemoveAction | undefined): onRemove is OnRemoveAction => {
    return typeof onRemove === 'function';
  };

  return (
    <ItemContainer>
      {index >= 1 && <HorizontalRuller />}

      <h2>Property{index >= 1 ? ` ${index + 1}` : ''}</h2>

      <FormInputWrapper title={FieldsLabels.FlatNumber} name={`properties.${index}.${PropertyField.FlatNumber}`}>
        <TextInput />
      </FormInputWrapper>

      <FormInputWrapper
        title={FieldsLabels.BuildingNumber}
        name={`properties.${index}.${PropertyField.BuildingNumber}`}>
        <TextInput />
      </FormInputWrapper>

      <FormInputWrapper title={FieldsLabels.BuildingName} name={`properties.${index}.${PropertyField.BuildingName}`}>
        <TextInput />
      </FormInputWrapper>

      <FormInputWrapper title={FieldsLabels.Street} name={`properties.${index}.${PropertyField.Street}`}>
        <TextInput />
      </FormInputWrapper>

      <FormInputWrapper title={FieldsLabels.City} name={`properties.${index}.${PropertyField.City}`}>
        <TextInput />
      </FormInputWrapper>

      <FormInputWrapper title={FieldsLabels.Postcode} name={`properties.${index}.${PropertyField.PostCode}`}>
        <PostcodeInput />
      </FormInputWrapper>

      <FormInputWrapper title={FieldsLabels.County} name={`properties.${index}.${PropertyField.County}`}>
        <TextInput />
      </FormInputWrapper>

      <FormInputWrapper
        title={FieldsLabels.DateOfPurchase}
        name={`properties.${index}.${PropertyField.DateOfPurchase}`}>
        <DateInput />
      </FormInputWrapper>

      <FormInputWrapper title={FieldsLabels.PurchasePrice} name={`properties.${index}.${PropertyField.PurchasePrice}`}>
        <CurrencyInput />
      </FormInputWrapper>

      <FormInputWrapper
        title={FieldsLabels.CurrentMortgageBalance}
        name={`properties.${index}.${PropertyField.CurrentMortgageBalance}`}>
        <CurrencyInput />
      </FormInputWrapper>

      {canRemoveItem(onRemove) && (
        <ActionButton type="button" onClick={() => onRemove(item.id as NonNullable<number>)}>
          Delete
        </ActionButton>
      )}
    </ItemContainer>
  );
};

interface Props {
  caseId: CaseId;
  control: any;
  reset: () => void;
}

const PropertyList = ({ control, caseId, reset }: Props) => {
  const { fields, append, remove } = useFieldArray({
    control,
    keyName: 'key',
    name: 'properties',
  });

  const { watch } = useFormContext();
  const haveProperty = watch(RepaymentStrategyField.HaveSaleOfAnotherProperty);
  useEffect(() => {
    if (haveProperty && fields.length === 0) {
      append(createProperty(null));
    }
  }, [haveProperty, append, fields.length]);

  const addNewProperty = async () => {
    try {
      append(createProperty(null));
    } catch (e) {}
  };

  const onRemoveItem = (index: number) => {
    remove(index);
  };

  return (
    <PropertyContainer>
      {fields.map((field, index) => {
        return (
          <PropertyItem
            key={field.key}
            item={(field as any) as Property}
            index={index}
            onRemove={fields.length > 1 ? () => onRemoveItem(index) : undefined}
          />
        );
      })}
      <ActionButton type="button" onClick={addNewProperty}>
        Add another
      </ActionButton>
    </PropertyContainer>
  );
};

export default PropertyList;
